import React from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import INRInvestorsPage from "./pages/INRInvestorsPage";
import INRInvestorFormPage from "./pages/INRInvestorFormPage";
import INRBulkUploadPage from "./pages/INRBulkUploadPage";
import InvestorsPage from "./pages/InvestorsPage";
import InvestorHistoryPage from "./pages/InvestorHistoryPage";
import SchemesPage from "./pages/SchemesPage";
import BulkUploadPage from "./pages/BulkUploadPage";
import InvestorFormPage from "./pages/InvestorFormPage";
import InvestorsFilterPage from "./pages/InvestorsFilterPage";
import INRInvestorsFilterPage from "./pages/INRInvestorsFilterPage";
import DeletedRecordsPage from "./pages/DeletedRecordsPage";
import InvestorAccessPage from "./pages/InvestorAccessPage";
import InvestorLoginPage from "./pages/InvestorLoginPage";
import InvestorDashboardPage from "./pages/InvestorDashboardPage";
import UserManagementPage from "./pages/UserManagementPage";
import MaturityPage from "./pages/MaturityPage";
import ProtectedRoute from "./components/ProtectedRoute";
import InvestorProtectedRoute from "./components/InvestorProtectedRoute";
import AuthGuard from "./components/AuthGuard";
import Layout from "./components/Layout";
import RoleBasedRedirect from "./components/RoleBasedRedirect";
import PermissionGuard from "./components/PermissionGuard";

// Access Denied component
const AccessDenied = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
      <p className="text-gray-600 mb-6">
        You don't have permission to access this page.
      </p>
      <Link
        to="/dashboard"
        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
      >
        Return to Dashboard
      </Link>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route
            path="/"
          element={
            <AuthGuard>
              <LoginPage />
            </AuthGuard>
          }
        />
        <Route
          path="/login"
          element={
            <AuthGuard>
              <LoginPage />
            </AuthGuard>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <DashboardPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/investors"
          element={
            <ProtectedRoute>
              <PermissionGuard
                permission="investors.view"
                fallback={<AccessDenied />}
              >
                <Layout>
                  <InvestorsPage />
                </Layout>
              </PermissionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/investors/:id/history"
          element={
            <ProtectedRoute>
              <PermissionGuard
                permission="investors.view"
                fallback={<AccessDenied />}
              >
                <Layout>
                  <InvestorHistoryPage />
                </Layout>
              </PermissionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/investors-filter"
          element={
            <ProtectedRoute>
              <PermissionGuard
                permission="investors.view"
                fallback={<AccessDenied />}
              >
                <Layout>
                  <InvestorsFilterPage />
                </Layout>
              </PermissionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/inr-investors-filter"
          element={
            <ProtectedRoute>
              <PermissionGuard
                permission="inr.view"
                fallback={<AccessDenied />}
              >
                <Layout>
                  <INRInvestorsFilterPage />
                </Layout>
              </PermissionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/schemes"
          element={
            <ProtectedRoute>
              <PermissionGuard
                permission="schemes.view"
                fallback={<AccessDenied />}
              >
                <Layout>
                  <SchemesPage />
                </Layout>
              </PermissionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bulk-upload"
          element={
            <ProtectedRoute>
              <PermissionGuard
                permission="investors.bulk_upload"
                fallback={<AccessDenied />}
              >
                <Layout>
                  <BulkUploadPage />
                </Layout>
              </PermissionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/investor-form"
          element={
            <ProtectedRoute>
              <PermissionGuard
                permission="investors.create"
                fallback={<AccessDenied />}
              >
                <Layout>
                  <InvestorFormPage />
                </Layout>
              </PermissionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/investor-access"
          element={
            <ProtectedRoute>
              <PermissionGuard
                permissions={["investors.view", "users.view"]}
                fallback={<AccessDenied />}
              >
                <Layout>
                  <InvestorAccessPage />
                </Layout>
              </PermissionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/inr-investors"
          element={
            <ProtectedRoute>
              <PermissionGuard
                permission="inr.view"
                fallback={<AccessDenied />}
              >
                <Layout>
                  <INRInvestorsPage />
                </Layout>
              </PermissionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/inr-investor-form"
          element={
            <ProtectedRoute>
              <PermissionGuard
                permission="inr.create"
                fallback={<AccessDenied />}
              >
                <Layout>
                  <INRInvestorFormPage />
                </Layout>
              </PermissionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/inr-bulk-upload"
          element={
            <ProtectedRoute>
              <PermissionGuard
                permission="inr.bulk_upload"
                fallback={<AccessDenied />}
              >
                <Layout>
                  <INRBulkUploadPage />
                </Layout>
              </PermissionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/deleted-records"
          element={
            <ProtectedRoute>
              <PermissionGuard
                permissions={["investors.view", "inr.view"]}
                fallback={<AccessDenied />}
              >
                <Layout>
                  <DeletedRecordsPage />
                </Layout>
              </PermissionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <PermissionGuard
                permission="users.view"
                fallback={<AccessDenied />}
              >
                <Layout>
                  <UserManagementPage />
                </Layout>
              </PermissionGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/maturity"
          element={
            <ProtectedRoute>
              <PermissionGuard
                permission="investors.view"
                fallback={<AccessDenied />}
              >
                <Layout>
                  <MaturityPage />
                </Layout>
              </PermissionGuard>
            </ProtectedRoute>
          }
        />
        <Route path="/investor-login" element={<InvestorLoginPage />} />
        <Route
          path="/investor-dashboard"
          element={
            <InvestorProtectedRoute>
              <Layout>
                <InvestorDashboardPage />
              </Layout>
            </InvestorProtectedRoute>
          }
        />
      </Routes>
    </Router>
    </AuthProvider>
  );
}

export default App;
